import React from 'react'
import { App, Button, Empty, Modal, Spin, Tabs } from 'antd'
import {
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    ReloadOutlined,
    SaveOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import {
    HistoryAnnotationModelOption,
    HistoryPrediction,
    HistoryRecord,
    HistoryReviewStatus,
} from '../../../types/history'
import { resolveHistoryDisplayPredictions } from '../../../utils/historyPredictions'
import { remapPredictionsForAnnotationModels } from '../../../utils/annotationModelCompatibility'
import type {
    AnnotationActionPlacement,
    AnnotationFooterActions,
    AnnotationZoomControlPlacement,
} from './DetectionAnnotationEditor'
import { ImageResultView } from './ImageResultView'

type ReviewComparisonTabKey = 'system' | 'review'

interface HistoryDetailProps {
    visible: boolean
    record: HistoryRecord | null
    loading?: boolean
    error?: string | null
    editable: boolean
    canExport: boolean
    bypassCapabilityFallback?: boolean
    reviewSubmitting: boolean
    exportSubmitting: boolean
    onClose: () => void
    onRetry?: () => void
    onUpdateReview: (
        status: HistoryReviewStatus,
        predictions?: HistoryPrediction[],
        annotationModelOptionKeys?: string[],
    ) => Promise<HistoryRecord | null>
    onSaveEditablePredictions?: (
        predictions: HistoryPrediction[],
    ) => Promise<HistoryRecord | null>
    onExport: (record: HistoryRecord) => Promise<void>
    formatDate: (timestamp: number) => string
    editablePredictions?: HistoryPrediction[]
    annotationActionPlacement?: AnnotationActionPlacement
    zoomControlPlacement?: AnnotationZoomControlPlacement
    defaultPredictionConfidence?: number
    readonlyPredictionSource?: 'model' | 'effective'
    readonlyPreviewOnImageClick?: boolean
    useManualRecognitionTypes?: boolean
    titleKey?: string
    editableTitleKey?: string
    saveButtonTextKey?: string
    resetButtonTextKey?: string
    showImageNormalAction?: boolean
    saveSuccessMessageKey?: string
    saveFailureMessageKey?: string
    requireEditActivation?: boolean
    showReviewComparisonTabs?: boolean
    reviewComparisonDefaultActiveKey?: ReviewComparisonTabKey
    showReviewComparisonSystemDetectionInfo?: boolean
    showAnnotationLabelsOnBoxes?: boolean
    showAnnotationTypeLegend?: boolean
    rawClassLocalizedNames?: Record<string, string>
}

export const HistoryDetail: React.FC<HistoryDetailProps> = ({
    visible,
    record,
    loading = false,
    error,
    editable,
    canExport,
    bypassCapabilityFallback = false,
    reviewSubmitting,
    exportSubmitting,
    onClose,
    onRetry,
    onUpdateReview,
    onSaveEditablePredictions,
    onExport,
    formatDate,
    editablePredictions,
    annotationActionPlacement = 'editor-toolbar',
    zoomControlPlacement = 'toolbar',
    defaultPredictionConfidence,
    readonlyPredictionSource,
    readonlyPreviewOnImageClick = false,
    useManualRecognitionTypes = false,
    titleKey,
    editableTitleKey = 'modal.annotationTitle',
    saveButtonTextKey = 'actions.saveAnnotations',
    resetButtonTextKey = 'actions.resetAnnotations',
    showImageNormalAction = true,
    saveSuccessMessageKey = 'messages.annotationSaved',
    saveFailureMessageKey = 'messages.annotationSaveFailed',
    requireEditActivation = false,
    showReviewComparisonTabs = false,
    reviewComparisonDefaultActiveKey = 'review',
    showReviewComparisonSystemDetectionInfo = false,
    showAnnotationLabelsOnBoxes = false,
    showAnnotationTypeLegend = false,
    rawClassLocalizedNames,
}) => {
    const { t } = useTranslation('history')
    const { message, notification } = App.useApp()
    const currentReviewStatus = record?.review?.status || 'pending'
    const [draftPredictions, setDraftPredictions] = React.useState<
        HistoryPrediction[]
    >([])
    const [annotationSeedPredictions, setAnnotationSeedPredictions] =
        React.useState<HistoryPrediction[]>([])
    const [annotationSeedVersion, setAnnotationSeedVersion] = React.useState(0)
    const [annotationDirty, setAnnotationDirty] = React.useState(false)
    const [isEditing, setIsEditing] = React.useState(!requireEditActivation)
    const [
        selectedAnnotationModelOptionKeys,
        setSelectedAnnotationModelOptionKeys,
    ] = React.useState<string[]>([])
    const [annotationFooterActions, setAnnotationFooterActions] =
        React.useState<AnnotationFooterActions | null>(null)
    const [activeReviewComparisonTab, setActiveReviewComparisonTab] =
        React.useState<ReviewComparisonTabKey>(reviewComparisonDefaultActiveKey)
    const useAnnotationFooterActions =
        annotationActionPlacement === 'modal-footer'
    const activeEditable = editable && (!requireEditActivation || isEditing)
    const isSystemReviewComparisonTabActive =
        showReviewComparisonTabs && activeReviewComparisonTab === 'system'
    const allowReviewActions = !isSystemReviewComparisonTabActive
    const modalTitle = titleKey
        ? t(titleKey)
        : activeEditable && allowReviewActions
          ? t(editableTitleKey)
          : t('modal.detailTitle')
    const isManualRecognitionEditor =
        activeEditable && annotationActionPlacement === 'modal-footer'
    const annotationModelOptions = React.useMemo(
        () => record?.annotation_context?.model_options ?? [],
        [record?.annotation_context?.model_options],
    )
    const selectedAnnotationModels = React.useMemo(
        () =>
            selectedAnnotationModelOptionKeys
                .map((optionKey) =>
                    annotationModelOptions.find(
                        (option) => option.option_key === optionKey,
                    ),
                )
                .filter((option): option is HistoryAnnotationModelOption =>
                    Boolean(option),
                ),
        [annotationModelOptions, selectedAnnotationModelOptionKeys],
    )

    const resolveEditablePredictions = React.useCallback(
        (targetRecord: HistoryRecord | null) => {
            if (Array.isArray(editablePredictions)) {
                return editablePredictions
            }
            if (
                isManualRecognitionEditor &&
                Array.isArray(targetRecord?.manual_recognition?.predictions)
            ) {
                return targetRecord.manual_recognition.predictions
            }
            if (isManualRecognitionEditor) {
                return Array.isArray(targetRecord?.result.predictions)
                    ? targetRecord.result.predictions
                    : []
            }
            const reviewPredictions = targetRecord?.review?.predictions
            if (Array.isArray(reviewPredictions)) {
                return reviewPredictions
            }

            return resolveHistoryDisplayPredictions(
                targetRecord?.result,
                bypassCapabilityFallback,
            )
        },
        [
            bypassCapabilityFallback,
            editablePredictions,
            isManualRecognitionEditor,
        ],
    )

    const referencePredictions = React.useMemo(() => {
        if (!isManualRecognitionEditor || !record?.result) {
            return undefined
        }

        if (isManualRecognitionEditor) {
            return []
        }

        if (Array.isArray(record.result.raw_predictions)) {
            return record.result.raw_predictions
        }

        return Array.isArray(record.result.predictions)
            ? record.result.predictions
            : []
    }, [isManualRecognitionEditor, record])

    React.useEffect(() => {
        const nextPredictions = resolveEditablePredictions(record)
        setDraftPredictions(nextPredictions)
        setAnnotationSeedPredictions(nextPredictions)
        setAnnotationSeedVersion((current) => current + 1)
        setAnnotationDirty(false)
        setIsEditing(!requireEditActivation)
        setSelectedAnnotationModelOptionKeys(
            record?.annotation_context?.default_model_option_keys ??
                (record?.annotation_context?.default_model_option_key
                    ? [record.annotation_context.default_model_option_key]
                    : []),
        )
    }, [record, requireEditActivation, resolveEditablePredictions, visible])

    React.useEffect(() => {
        if (visible && showReviewComparisonTabs) {
            setActiveReviewComparisonTab(reviewComparisonDefaultActiveKey)
        }
    }, [reviewComparisonDefaultActiveKey, showReviewComparisonTabs, visible])

    const systemComparisonRecord = React.useMemo<HistoryRecord | null>(() => {
        if (!record || !showReviewComparisonTabs) {
            return null
        }

        return {
            ...record,
            manual_recognition: null,
            review: null,
            effective_source: 'model',
            effective_predictions: Array.isArray(record.result.predictions)
                ? record.result.predictions
                : [],
            effective_annotated_image: record.result.annotated_image ?? null,
            result: {
                ...record.result,
                raw_predictions: undefined,
            },
        }
    }, [record, showReviewComparisonTabs])

    const syncPredictionsFromRecord = (nextRecord: HistoryRecord) => {
        const nextPredictions = resolveEditablePredictions(nextRecord)
        setDraftPredictions(nextPredictions)
        setAnnotationSeedPredictions(nextPredictions)
        setAnnotationSeedVersion((current) => current + 1)
        setAnnotationDirty(false)
    }

    const persistReview = async (
        status: HistoryReviewStatus,
        successMessage: string,
        failureMessage: string,
        predictions: HistoryPrediction[] = draftPredictions,
    ) => {
        const updatedRecord = await onUpdateReview(
            status,
            predictions,
            selectedAnnotationModelOptionKeys.length > 0
                ? selectedAnnotationModelOptionKeys
                : undefined,
        )
        if (updatedRecord) {
            syncPredictionsFromRecord(updatedRecord)
            message.success(successMessage)
            return updatedRecord
        }

        message.error(failureMessage)
        return null
    }

    const handleSaveAnnotations = async () => {
        const updatedRecord = onSaveEditablePredictions
            ? await onSaveEditablePredictions(draftPredictions)
            : await persistReview(
                  'needs_review',
                  t(saveSuccessMessageKey),
                  t(saveFailureMessageKey),
              )
        if (onSaveEditablePredictions) {
            if (updatedRecord) {
                syncPredictionsFromRecord(updatedRecord)
                message.success(t(saveSuccessMessageKey))
            } else {
                message.error(t(saveFailureMessageKey))
            }
        }
        if (updatedRecord) {
            onClose()
        }
    }

    const handleMarkImageNormal = async () => {
        const updatedRecord = onSaveEditablePredictions
            ? await onSaveEditablePredictions([])
            : await persistReview(
                  'confirmed',
                  t('messages.imageMarkedNormal'),
                  t('messages.imageMarkNormalFailed'),
                  [],
              )
        if (onSaveEditablePredictions) {
            if (updatedRecord) {
                syncPredictionsFromRecord(updatedRecord)
                message.success(t('messages.imageMarkedNormal'))
            } else {
                message.error(t('messages.imageMarkNormalFailed'))
            }
        }
        if (updatedRecord) {
            onClose()
        }
    }

    const handleExportAction = async () => {
        if (!record) {
            return
        }

        let exportRecord = record
        if (annotationDirty && activeEditable) {
            const updatedRecord = await onUpdateReview(
                'needs_review',
                draftPredictions,
                selectedAnnotationModelOptionKeys.length > 0
                    ? selectedAnnotationModelOptionKeys
                    : undefined,
            )
            if (!updatedRecord) {
                message.error(t('messages.annotationSaveFailed'))
                return
            }

            syncPredictionsFromRecord(updatedRecord)
            exportRecord = updatedRecord
        }

        await onExport(exportRecord)
    }

    const handlePredictionsChange = React.useCallback(
        (predictions: HistoryPrediction[]) => {
            setDraftPredictions(predictions)
            setAnnotationDirty(true)
        },
        [],
    )

    const handleResetPredictions = React.useCallback(() => {
        const nextPredictions =
            selectedAnnotationModelOptionKeys.length > 0
                ? annotationSeedPredictions
                : resolveEditablePredictions(record)
        setDraftPredictions(nextPredictions)
        setAnnotationSeedPredictions(nextPredictions)
        setAnnotationSeedVersion((current) => current + 1)
        setAnnotationDirty(selectedAnnotationModelOptionKeys.length > 0)
        setAnnotationFooterActions(null)
    }, [
        annotationSeedPredictions,
        record,
        resolveEditablePredictions,
        selectedAnnotationModelOptionKeys.length,
    ])

    const handleStartEditing = () => {
        if (
            selectedAnnotationModels.length === 0 ||
            selectedAnnotationModels.length !==
                selectedAnnotationModelOptionKeys.length
        ) {
            message.error(t('messages.annotationModelUnavailable'))
            return
        }

        const remapped = remapPredictionsForAnnotationModels(
            resolveEditablePredictions(record),
            selectedAnnotationModels,
        )
        setDraftPredictions(remapped.predictions)
        setAnnotationSeedPredictions(remapped.predictions)
        setAnnotationSeedVersion((current) => current + 1)
        setAnnotationDirty(true)
        setAnnotationFooterActions(null)
        setIsEditing(true)

        if (remapped.removedIdentityKeys.length > 0) {
            notification.warning({
                duration: 0,
                key: 'annotation-incompatible-boxes-removed',
                message: t('messages.annotationIncompatibleBoxesRemoved', {
                    count: remapped.removedIdentityKeys.length,
                }),
                placement: 'top',
            })
        }
    }

    const renderReviewResultView = () =>
        record ? (
            <ImageResultView
                record={record}
                editable={activeEditable}
                annotationSeedPredictions={annotationSeedPredictions}
                referencePredictions={referencePredictions}
                annotationSeedKey={`${record.id}-${annotationSeedVersion}`}
                annotationDirty={annotationDirty}
                annotationLabelOptions={
                    activeEditable
                        ? selectedAnnotationModels.flatMap(
                              (model) => model.label_options,
                          )
                        : undefined
                }
                bypassCapabilityFallback={bypassCapabilityFallback}
                readonlyPredictionSource={readonlyPredictionSource}
                readonlyPreviewOnImageClick={readonlyPreviewOnImageClick}
                useManualRecognitionTypes={useManualRecognitionTypes}
                showAnnotationLabelsOnBoxes={showAnnotationLabelsOnBoxes}
                showAnnotationTypeLegend={showAnnotationTypeLegend}
                rawClassLocalizedNames={rawClassLocalizedNames}
                onPredictionsChange={handlePredictionsChange}
                annotationActionPlacement={annotationActionPlacement}
                zoomControlPlacement={zoomControlPlacement}
                defaultPredictionConfidence={defaultPredictionConfidence}
                onAnnotationFooterActionsChange={
                    useAnnotationFooterActions
                        ? setAnnotationFooterActions
                        : undefined
                }
                onResetPredictions={handleResetPredictions}
                formatDate={formatDate}
            />
        ) : null

    const renderSystemComparisonResultView = () =>
        systemComparisonRecord ? (
            <ImageResultView
                record={systemComparisonRecord}
                editable={false}
                annotationSeedPredictions={[]}
                annotationSeedKey={`${systemComparisonRecord.id}-system`}
                annotationDirty={false}
                bypassCapabilityFallback={bypassCapabilityFallback}
                readonlyPredictionSource="effective"
                readonlyPreviewOnImageClick
                showReadonlyDetectionInfo={
                    showReviewComparisonSystemDetectionInfo
                }
                showAnnotationLabelsOnBoxes={showAnnotationLabelsOnBoxes}
                showAnnotationTypeLegend={showAnnotationTypeLegend}
                rawClassLocalizedNames={rawClassLocalizedNames}
                onPredictionsChange={() => undefined}
                onResetPredictions={() => undefined}
                formatDate={formatDate}
            />
        ) : null

    return (
        <>
            <Modal
                title={modalTitle}
                open={visible}
                onCancel={onClose}
                width={activeEditable ? '96vw' : '64vw'}
                maskClosable={!(activeEditable && allowReviewActions)}
                style={{
                    top: activeEditable ? 16 : 48,
                    maxWidth: activeEditable ? 1680 : 920,
                }}
                styles={{
                    body: {
                        maxHeight: activeEditable
                            ? 'calc(100vh - 180px)'
                            : 'calc(100vh - 180px)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    },
                    footer: activeEditable
                        ? {
                              position: 'sticky',
                              bottom: 0,
                              zIndex: 10,
                              background: '#ffffff',
                              borderTop: '1px solid #f0f0f0',
                          }
                        : undefined,
                }}
                footer={[
                    ...(record &&
                    activeEditable &&
                    allowReviewActions &&
                    useAnnotationFooterActions &&
                    annotationFooterActions
                        ? [
                              <Button
                                  key="delete-selected-box"
                                  icon={<DeleteOutlined />}
                                  disabled={
                                      annotationFooterActions.deleteDisabled
                                  }
                                  onClick={
                                      annotationFooterActions.onDeleteSelected
                                  }
                              >
                                  {t('actions.deleteSelectedBox')}
                              </Button>,
                              <Button
                                  key="reset-annotations"
                                  icon={<ReloadOutlined />}
                                  disabled={
                                      annotationFooterActions.resetDisabled
                                  }
                                  onClick={annotationFooterActions.onReset}
                              >
                                  {t(resetButtonTextKey)}
                              </Button>,
                          ]
                        : []),
                    ...(record && activeEditable && allowReviewActions
                        ? [
                              ...(showImageNormalAction
                                  ? [
                                        <Button
                                            key="mark-image-normal"
                                            type={
                                                currentReviewStatus ===
                                                    'confirmed' &&
                                                !annotationDirty
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                            loading={reviewSubmitting}
                                            onClick={handleMarkImageNormal}
                                        >
                                            {t('actions.imageNormal')}
                                        </Button>,
                                    ]
                                  : []),
                              <Button
                                  key="save-annotations"
                                  type="primary"
                                  icon={<SaveOutlined />}
                                  disabled={!annotationDirty}
                                  loading={reviewSubmitting}
                                  onClick={handleSaveAnnotations}
                              >
                                  {t(saveButtonTextKey)}
                              </Button>,
                          ]
                        : []),
                    ...(record &&
                    editable &&
                    allowReviewActions &&
                    requireEditActivation &&
                    !isEditing
                        ? [
                              <Button
                                  key="edit-annotations"
                                  type="primary"
                                  icon={<EditOutlined />}
                                  onClick={handleStartEditing}
                              >
                                  {t('actions.editAnnotations')}
                              </Button>,
                          ]
                        : []),
                    ...(record && canExport && allowReviewActions
                        ? [
                              <Button
                                  key="export"
                                  icon={<DownloadOutlined />}
                                  loading={exportSubmitting}
                                  onClick={handleExportAction}
                              >
                                  {t('actions.exportDataset')}
                              </Button>,
                          ]
                        : []),
                    <Button key="close" onClick={onClose}>
                        {t('actions.close')}
                    </Button>,
                ]}
            >
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 48 }}>
                        <Spin size="large" />
                    </div>
                ) : error ? (
                    <Empty
                        description={
                            <div>
                                <div>{error}</div>
                                {onRetry ? (
                                    <Button
                                        style={{ marginTop: 12 }}
                                        onClick={onRetry}
                                    >
                                        {t('actions.refresh')}
                                    </Button>
                                ) : null}
                            </div>
                        }
                    />
                ) : record ? (
                    showReviewComparisonTabs ? (
                        <Tabs
                            activeKey={activeReviewComparisonTab}
                            onChange={(key) =>
                                setActiveReviewComparisonTab(
                                    key as ReviewComparisonTabKey,
                                )
                            }
                            items={[
                                {
                                    key: 'system',
                                    label: t('detail.systemResult'),
                                    children:
                                        renderSystemComparisonResultView(),
                                },
                                {
                                    key: 'review',
                                    label: t('detail.reviewResult'),
                                    children: renderReviewResultView(),
                                },
                            ]}
                        />
                    ) : (
                        renderReviewResultView()
                    )
                ) : (
                    <Empty description={t('messages.noRecords')} />
                )}
            </Modal>
        </>
    )
}
